import React, { useMemo, useState } from "react"
import { StandardDialog } from "../../../../../../../shared/components/dialogs/StandardDialog"
import { LocalizedStrict } from "../../../../../../localization/components/AppLocalized"
import { Box, Stack } from "@mui/material"
import { makeStyles } from "tss-react/mui"
import { CreateOrEditPriceListFormValues, Customer, CustomerFlat } from "../../../priceListTypes"
import { StandardButton } from "../../../../../../../shared/components/buttons/StandardButton"
import { useApiQuery } from "../../../../../../../core/api/useApiQuery"
import { fetchCustomersForMerchant, FetchCustomersResponse } from "../../../../../../customers/customerApi"
import { useMerchant } from "../../../../../../merchants/merchantHooks"
import { MRT_ColumnDef, MRT_RowSelectionState } from "material-react-table"
import { useApiQueryTable } from "../../../../../../../core/api/components/ApiQueryTable"
import { useAppLocalization } from "../../../../../../localization/components/AppLocalizationProvider"
import { useFormikContext } from "formik"
import { convertCustomersByUserGroup, convertFlatToCustomers } from "../../../helpers"

const useStyles = makeStyles()((_theme) => ({
    dialogContainer: {
        padding: "2.5rem",
        color: _theme.palette.tailwind.slate[700],
    },
    tableContainer: {
        maxHeight: "100%",
        borderRadius: ".75rem",
        padding: "1em 1em 3em 1em",
    },
    tablePaper: {
        boxShadow: "none",
        borderRadius: ".75rem",
    },
    tableHeadCell: {
        border: "none",
        color: _theme.palette.tailwind.slate[500],
    },
    tableHeadRow: {
        boxShadow: "none",
        background: _theme.palette.tailwind.slate[50],
    },
    topToolbar: {
        borderRadius: ".75rem",
    },
    headCellRowExpand: {
        padding: "1rem",
        border: "none",
    },
}))

export function transformToFlatCustomerData(data: FetchCustomersResponse): CustomerFlat[] {
    const transformedData: CustomerFlat[] = []

    data.customers.forEach((customer) => {
        if (customer.userGroups && customer.userGroups.length === 0) {
            transformedData.push({
                customerId: customer.id,
                customerName: customer.name,
                isOfficeHotel: customer.isOfficeHotel,
                userGroupId: customer.id,
                userGroupName: "",
            })
        } else {
            customer?.userGroups &&
                customer?.userGroups.forEach((userGroup) => {
                    transformedData.push({
                        customerId: customer.id,
                        isOfficeHotel: customer.isOfficeHotel,
                        customerName: customer.name,
                        userGroupId: userGroup.id,
                        userGroupName: userGroup.name,
                    })
                })
        }
    })

    return transformedData
}

function createInitialRowSelection(customers: Customer[]) {
    const initialSelection: {
        [key: string]: boolean
    } = {}

    const customersFlat = convertCustomersByUserGroup(customers)

    customersFlat.forEach((customer) => {
        initialSelection[customer.userGroupId] = true
    })
    return initialSelection
}

type AddCustomerDialogProps = Readonly<{
    open: boolean
    onClose: () => void
}>

export function AddCustomerDialog({ open, onClose }: AddCustomerDialogProps) {
    const { classes } = useStyles()

    const { l10n } = useAppLocalization()
    const { merchantId } = useMerchant()

    const { setFieldValue, values } = useFormikContext<CreateOrEditPriceListFormValues>()
    const [rowSelection, setRowSelection] = useState<MRT_RowSelectionState>(() =>
        createInitialRowSelection(values.customers),
    )

    const customersResult = useApiQuery(fetchCustomersForMerchant, {
        queryName: "customers-for-merchant",
        dependencies: { merchantId },
        select: transformToFlatCustomerData,
    })

    const addSelectedCustomers = () => {
        if (!customersResult.data) return

        const selectedIds = Object.keys(rowSelection).filter((key) => rowSelection[key])

        const selectedCustomers = convertFlatToCustomers(
            customersResult.data.filter((customer) => selectedIds.includes(customer.userGroupId.toString())),
        )

        setFieldValue("customers", selectedCustomers)

        onClose()
    }

    const { ApiQueryTable, columnFactory } = useApiQueryTable(customersResult)

    const columns = useMemo<MRT_ColumnDef<CustomerFlat>[]>(
        () => [
            columnFactory.nameColumn({
                header: l10n.getString("create-or-edit-price-list-customer-table-header-name"),
                accessorKey: "customerName",
            }),
            columnFactory.nameColumn({
                header: l10n.getString("create-or-edit-price-list-customer-table-header-user-group"),
                accessorKey: "userGroupName",
            }),
        ],
        [l10n, columnFactory],
    )

    return (
        <StandardDialog
            scroll="body"
            maxWidthProp="lg"
            open={open}
            onClose={onClose}
            dialogTitle={
                <LocalizedStrict id="create-or-edit-price-list-customer-table-add-customers-title">
                    <>Add customers</>
                </LocalizedStrict>
            }
        >
            <Box p="1.25rem">
                <Box className={classes.dialogContainer}>
                    <ApiQueryTable
                        columns={columns}
                        enableColumnOrdering
                        enableRowSelection
                        enableStickyHeader
                        enableClickToCopy={false}
                        enableDensityToggle={false}
                        enableHiding={false}
                        enableRowActions={false}
                        enableColumnDragging={false}
                        enableBottomToolbar={false}
                        enableToolbarInternalActions={false}
                        onRowSelectionChange={setRowSelection}
                        state={{
                            rowSelection,
                        }}
                        getRowId={(originalRow, index) => {
                            if (!originalRow.userGroupId) return index.toString()

                            return originalRow.userGroupId.toString()
                        }}
                        initialState={{ density: "spacious" }}
                        muiTablePaperProps={{ className: classes.tablePaper }}
                        muiTableContainerProps={{ className: classes.tableContainer }}
                        muiTableHeadCellProps={{ className: classes.tableHeadCell }}
                        muiTableHeadRowProps={{ className: classes.tableHeadRow }}
                        muiTopToolbarProps={{ className: classes.topToolbar }}
                    />

                    <Stack direction="column" pt="2.5rem" alignItems="center" spacing={2.5}>
                        <LocalizedStrict id="create-or-edit-price-list-customer-table-add-button">
                            <StandardButton fullWidth={false} onClick={addSelectedCustomers}>
                                Add
                            </StandardButton>
                        </LocalizedStrict>
                    </Stack>
                </Box>
            </Box>
        </StandardDialog>
    )
}
